/**
 * Admin password-reset modal. Link-first: the safe default emails the user a reset
 * link (no one learns their password). The "temporary password" path is deliberately
 * higher-friction (typed email confirmation) because it signs the user out and the
 * admin knows the password. All enforcement is server-side in the manage-user edge
 * function; this UI mirrors it and never displays generated passwords.
 */
import { useState } from "react";
import { supabase } from "../../services/supabase";
import type { Profile } from "../../types/auth.types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: Profile | null;
}

type Method = "link" | "temp";
const MIN_TEMP_LEN = 12;

export default function ResetPasswordModal({ isOpen, onClose, user }: Props) {
  const [method, setMethod] = useState<Method>("link");
  const [tempPw, setTempPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState<string>("");

  if (!isOpen || !user) {
    return null;
  }

  const reset = () => {
    setMethod("link");
    setTempPw("");
    setShowPw(false);
    setConfirmEmail("");
    setError("");
    setDone("");
    setBusy(false);
  };
  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    setError("");
    if (method === "temp") {
      if (tempPw.length < MIN_TEMP_LEN) {
        setError(
          `Temporary password must be at least ${MIN_TEMP_LEN} characters.`,
        );
        return;
      }
      if (
        confirmEmail.trim().toLowerCase() !== (user.email || "").toLowerCase()
      ) {
        setError("Type the user's email to confirm this disruptive action.");
        return;
      }
    }
    setBusy(true);
    const action = method === "link" ? "send_reset_link" : "set_temp_password";
    const { data, error: invErr } = await supabase.functions.invoke(
      "manage-user",
      {
        body: {
          action,
          user_id: user.id,
          ...(method === "temp" ? { password: tempPw } : {}),
        },
      },
    );
    setBusy(false);
    if (invErr || data?.error || !data?.success) {
      setError(
        data?.error || invErr?.message || "Could not complete the reset.",
      );
      return;
    }
    if (method === "link") {
      setDone(`Reset link sent to ${user.email}. No password was changed.`);
    } else {
      const sess = data.sessions_revoked
        ? "existing sessions were revoked"
        : "session revocation could not be confirmed";
      setDone(
        `Temporary password set. ${user.full_name || user.email} must change it at next login; ${sess}.`,
      );
    }
    setTempPw("");
    setConfirmEmail("");
  };

  const name = user.full_name || user.email;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Reset password"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-lg font-bold text-gray-900">Reset password</h2>
        <p className="mt-1 text-sm text-gray-600">
          {name} · <span className="text-gray-500">{user.email}</span>
        </p>

        {done ? (
          <>
            <p
              role="status"
              className="mt-4 rounded-lg bg-green-50 px-3 py-3 text-sm text-green-800"
            >
              {done}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                onClick={close}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <fieldset className="mt-4 space-y-2">
              <legend className="sr-only">Reset method</legend>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3">
                <input
                  type="radio"
                  name="reset-method"
                  checked={method === "link"}
                  onChange={() => setMethod("link")}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    Send reset link (recommended)
                  </span>
                  <span className="block text-xs text-gray-500">
                    What the user sees: an email with a link to set their own
                    password. No password is changed now.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3">
                <input
                  type="radio"
                  name="reset-method"
                  checked={method === "temp"}
                  onChange={() => setMethod("temp")}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    Set a temporary password
                  </span>
                  <span className="block text-xs text-gray-500">
                    What the user sees: signed out everywhere; must change this
                    password at next login. Use only when they can't get email.
                  </span>
                </span>
              </label>
            </fieldset>

            {method === "temp" && (
              <div className="mt-3 space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div>
                  <label
                    htmlFor="temp-pw"
                    className="block text-xs font-medium text-gray-700 mb-1"
                  >
                    Temporary password (min {MIN_TEMP_LEN} chars)
                  </label>
                  <div className="relative">
                    <input
                      id="temp-pw"
                      type={showPw ? "text" : "password"}
                      value={tempPw}
                      onChange={(e) => setTempPw(e.target.value)}
                      autoComplete="new-password"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-16 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((s) => !s)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                      className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-blue-600"
                    >
                      {showPw ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="confirm-email"
                    className="block text-xs font-medium text-gray-700 mb-1"
                  >
                    Type <span className="font-mono">{user.email}</span> to
                    confirm
                  </label>
                  <input
                    id="confirm-email"
                    type="text"
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    autoCapitalize="off"
                    autoCorrect="off"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <p className="text-xs text-amber-900">
                  This signs the user out and requires them to change the
                  password at next login.
                </p>
              </div>
            )}

            {error && (
              <p
                role="alert"
                className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {error}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={close}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy
                  ? "Working…"
                  : method === "link"
                    ? "Send reset link"
                    : "Set temporary password"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
